import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/core/lib/queryKeys';
import { useTenant } from '@/core/hooks/useTenant';
import { useToast } from '@/shared/ui/use-toast';
import { useLanguage } from '@/core/LanguageContext';
import {
  startCompletion,
  saveResponse,
  completeCompletion,
  cancelCompletion,
  fetchCompletionById,
  fetchCompletionSummary,
} from '../api/completions';
import type { CompleteCompletionInput, SaveResponseInput } from '../types';

export function useCompletionById(completionId: string | null | undefined) {
  const { selectedTenantId } = useTenant();

  return useQuery({
    queryKey: queryKeys.modLpaCompletionDetail(completionId ?? null, selectedTenantId),
    queryFn: () => fetchCompletionById(completionId!, selectedTenantId ?? undefined),
    enabled: !!completionId && !!selectedTenantId,
  });
}

export function useCompletionSummary(completionId: string | null | undefined) {
  const { selectedTenantId } = useTenant();

  return useQuery({
    queryKey: queryKeys.modLpaCompletionSummary(completionId ?? null, selectedTenantId),
    queryFn: () => fetchCompletionSummary(completionId!, selectedTenantId ?? undefined),
    enabled: !!completionId && !!selectedTenantId,
  });
}

export function useStartCompletion() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (instanceId: string) => startCompletion(instanceId, selectedTenantId ?? undefined),
    onSuccess: (completion) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaCompletionDetail(completion.id, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: ['mod_lpa_auditor_instances'],
      });
      toast({
        title: t('mod_lpa.completions.startSuccess', 'Audit started'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.completions.startError', 'Failed to start audit'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useSaveCompletionResponse() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ completionId, input }: { completionId: string; input: SaveResponseInput }) =>
      saveResponse(completionId, input, selectedTenantId ?? undefined),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaCompletionDetail(response.completion_id, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaCompletionSummary(response.completion_id, selectedTenantId),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.completions.saveResponseError', 'Failed to save response'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useCompleteCompletion() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ completionId, input }: { completionId: string; input?: CompleteCompletionInput }) =>
      completeCompletion(completionId, input, selectedTenantId ?? undefined),
    onSuccess: (completion) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaCompletionDetail(completion.id, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaCompletionSummary(completion.id, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: ['mod_lpa_auditor_instances'],
      });
      toast({
        title: t('mod_lpa.completions.completeSuccess', 'Audit completed'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.completions.completeError', 'Failed to complete audit'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useCancelCompletion() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (completionId: string) => cancelCompletion(completionId, selectedTenantId ?? undefined),
    onSuccess: (completion) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaCompletionDetail(completion.id, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaCompletionSummary(completion.id, selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: ['mod_lpa_auditor_instances'],
      });
      toast({
        title: t('mod_lpa.completions.cancelSuccess', 'Audit cancelled'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.completions.cancelError', 'Failed to cancel audit'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
